import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, X, Check, Loader2 } from 'lucide-react';
import AudioRecorder from '../lib/AudioRecorder';

/**
 * AudioMessageRecorder - Voice message recording UI component
 * Inspired by Rocket.Chat's AudioMessageRecorder
 */
const AudioMessageRecorder = ({ onRecordingComplete, onCancel, disabled }) => {
  const [state, setState] = useState('idle'); // 'idle' | 'recording' | 'loading'
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const timerRef = useRef(null);

  // Format duration as MM:SS
  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Start the timer
  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      if (recorderRef.current) {
        setDuration(recorderRef.current.getDuration());
      }
    }, 100);
  }, []);

  // Stop the timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (recorderRef.current) {
        recorderRef.current.cancel();
      }
    };
  }, [stopTimer]);

  // Start recording
  const handleStartRecording = async () => {
    if (!AudioRecorder.isSupported()) {
      setError('Audio recording is not supported in this browser');
      return;
    }

    try {
      setError(null);
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.start();
      setState('recording');
      setDuration(0);
      startTimer();
    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone permission denied. Please allow access to record audio.');
      } else {
        setError(err.message || 'Failed to start recording');
      }
      setState('idle');
    }
  };

  // Cancel recording
  const handleCancel = useCallback(() => {
    stopTimer();
    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
    }
    setState('idle');
    setDuration(0);
    if (onCancel) {
      onCancel();
    }
  }, [stopTimer, onCancel]);

  // Finish recording
  const handleFinish = async () => {
    if (!recorderRef.current || state !== 'recording') return;

    stopTimer();
    setState('loading');

    try {
      const result = await recorderRef.current.stop();
      recorderRef.current = null;

      if (result.blob && result.blob.size > 0) {
        // Create a File object from the Blob
        const filename = `Audio_message_${Date.now()}.${result.extension}`;
        const file = new File([result.blob], filename, { type: result.mimeType });

        if (onRecordingComplete) {
          onRecordingComplete({
            file,
            blob: result.blob,
            mimeType: result.mimeType,
            duration: result.duration,
            filename,
          });
        }
      }

      setState('idle');
      setDuration(0);
    } catch (err) {
      console.error('Failed to finish recording:', err);
      setError(err.message || 'Failed to save recording');
      setState('idle');
    }
  };

  // Render idle state (microphone button)
  if (state === 'idle' && !error) {
    return (
      <button
        type="button"
        onClick={handleStartRecording}
        disabled={disabled}
        className={`p-2 rounded-full transition-colors ${
          disabled
            ? 'text-slate-400 cursor-not-allowed'
            : 'text-slate-500 hover:text-yellow-600 hover:bg-yellow-50 dark:text-slate-400 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20'
        }`}
        title="Record voice message"
      >
        <Mic className="w-5 h-5" />
      </button>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        <button
          type="button"
          onClick={() => setError(null)}
          className="p-1 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Render recording/loading state
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
      {/* Cancel button */}
      <button
        type="button"
        onClick={handleCancel}
        disabled={state === 'loading'}
        className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
        title="Cancel recording"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Recording indicator and timer */}
      <div className="flex items-center gap-2 flex-1">
        {state === 'recording' && (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
        )}
        {state === 'loading' && (
          <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />
        )}
        <span className="font-mono text-sm text-slate-700 dark:text-slate-300 min-w-[48px]">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Finish button */}
      <button
        type="button"
        onClick={handleFinish}
        disabled={state === 'loading' || duration < 500}
        className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Finish recording"
      >
        {state === 'loading' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Check className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};

export default AudioMessageRecorder;
